import { computed, ref } from 'vue'

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

const deferred = ref<BeforeInstallPromptEvent | null>(null)
const installed = ref(false)
const ios = ref(false)
let listening = false

function isIosDevice(): boolean {
  const ua = navigator.userAgent
  const iOS = /iPad|iPhone|iPod/.test(ua)
  const iPadOs = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1
  return iOS || iPadOs
}

/** Home-screen launch. A browser tab stays `display-mode: browser`. */
export function isInstalledPwa(): boolean {
  if (typeof window === 'undefined') return false
  const standalone = window.matchMedia('(display-mode: standalone)').matches
  const fullscreen = window.matchMedia('(display-mode: fullscreen)').matches
  const minimal = window.matchMedia('(display-mode: minimal-ui)').matches
  const native = (navigator as { standalone?: boolean }).standalone
  return standalone || fullscreen || minimal || !!native
}

function isStandaloneDisplay(): boolean {
  return isInstalledPwa()
}

function onPrompt(event: Event): void {
  event.preventDefault()
  deferred.value = event as BeforeInstallPromptEvent
}

function onInstalled(): void {
  installed.value = true
  deferred.value = null
}

/**
 * Attach before the Vue tree mounts. Chrome may fire `beforeinstallprompt`
 * once; a listener that only starts in `onMounted` can miss it.
 */
export function listenForPwaInstall(): void {
  if (listening || typeof window === 'undefined') return
  listening = true
  installed.value = isStandaloneDisplay()
  ios.value = isIosDevice()
  window.addEventListener('beforeinstallprompt', onPrompt)
  window.addEventListener('appinstalled', onInstalled)
}

/**
 * Chrome/Edge install prompt plus iOS “Add to Home Screen” copy.
 * Shared across Home, Settings, and the phone menu.
 */
export function usePwaInstall() {
  listenForPwaInstall()

  const canPrompt = computed(() => !!deferred.value && !installed.value)
  const showIosHint = computed(() => ios.value && !installed.value)
  const visible = computed(
    () => !installed.value && (canPrompt.value || showIosHint.value),
  )

  async function install(): Promise<void> {
    const event = deferred.value
    if (!event) return
    await event.prompt()
    const { outcome } = await event.userChoice
    if (outcome === 'accepted') {
      installed.value = true
      deferred.value = null
    }
  }

  return {
    installed,
    canPrompt,
    showIosHint,
    visible,
    install,
  }
}
